import React, { useContext, useMemo, useState } from 'react';
import { Text } from 'components';
import { Card, Title, Icon, Button } from '@bb/elements-web';
import styled from 'styled-components';
import { AnsweringServiceType } from '@bb/domain/dist/types/AnsweringSubscription';
import AnsweringServiceContext, {
  AnsweringServiceSubscribeStep,
} from '../AnsweringServiceContext';
import CancelModal from './CancelModal';
import { TitleCircle } from '../StyledComponents';

/**
 * The card displaying the information about the user's current active subscription plan.
 * To be used above the Listings table.
 */
const CurrentSubscriptionCard = () => {
  const {
    currentSubscriptionType,
    hasActiveAnsweringService,
    setCurrentStep,
    subscriptionPayer,
    viewingLevel,
  } = useContext(AnsweringServiceContext);

  /** Whether the modal confirming the user will cancel their sub is open. */
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  /** If the user or brokerage is the one paying for their own subscription. */
  const paysForSelf = useMemo(
    () =>
      (subscriptionPayer === 'brokerage' && viewingLevel === 'org') ||
      (subscriptionPayer === 'user' && viewingLevel === 'user'),
    [subscriptionPayer, viewingLevel],
  );

  if (hasActiveAnsweringService) {
    return (
      <StyledCard dataCy="CurrentSubscriptionCard">
        {currentSubscriptionType === AnsweringServiceType.AppointmentDesk && (
          <StyledTitleContainer>
            <TitleCircle style={{ marginRight: '1em' }}>
              <Icon icon="appointmentDesk" size={26} color="primary" />
            </TitleCircle>
            <Title level={4} dataCy="listing-toggle-title">
              Appointment Desk
            </Title>
          </StyledTitleContainer>
        )}
        {currentSubscriptionType === AnsweringServiceType.LiveConciergePlus && (
          <StyledTitleContainer>
            <TitleCircle style={{ marginRight: '1em' }}>
              <Icon
                icon="liveConcierge"
                size={26}
                color="primary"
                style={{ display: 'flex' }}
              />
            </TitleCircle>
            <Title level={2} dataCy="listing-toggle-title">
              Live Concierge
            </Title>
          </StyledTitleContainer>
        )}
        <StyledPlanContainer>
          <StyledPlanInfo>
            {hasActiveAnsweringService && (
              <StyledText type="secondary" dataCy="current-plan-text">
                Current Plan
              </StyledText>
            )}
            {subscriptionPayer !== 'user' && (
              <StyledManagedBy dataCy="managed-by-text">
                Managed by{' '}
                {subscriptionPayer === 'boardOrMls'
                  ? 'Organization'
                  : 'Brokerage'}
              </StyledManagedBy>
            )}
          </StyledPlanInfo>
          <StyledPlanActions>
            {currentSubscriptionType ===
              AnsweringServiceType.AppointmentDesk && (
              <Button
                type="primary"
                onClick={() =>
                  // When upgrading from Appt Desk, we push them to the Learn More page after Manage Listings.
                  setCurrentStep(AnsweringServiceSubscribeStep.LearnMore)
                }
                dataCy="upgrade-button"
              >
                Upgrade Plan
              </Button>
            )}
            {paysForSelf && (
              <Button
                type="secondary"
                onClick={() => setCancelModalOpen(true)}
                dataCy="cancel-button"
              >
                Cancel Plan
              </Button>
            )}
          </StyledPlanActions>
        </StyledPlanContainer>
        <CancelModal
          open={cancelModalOpen}
          onCancel={() => setCancelModalOpen(false)}
        />
      </StyledCard>
    );
  }
};

export default CurrentSubscriptionCard;

const StyledCard = styled(Card)`
  padding: 0 2%;
`;

const StyledManagedBy = styled(Text)`
  font-size: 20px;
`;

const StyledText = styled(Text)`
  font-size: 16px;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 1.5em;
`;

const StyledPlanContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const StyledPlanInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const StyledPlanActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;
